import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage, useIntl } from 'react-intl';

import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import { useLivestreams } from '@components/LivestreamBanner/hooks/useLivestreams';
import { ICharacteristic } from '@containers/ProductListing/model';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { isEmpty } from '@helpers/utils';
import { ExpandableText } from '@sharedComponents/ExpandableText';
import { trackTab } from '@sharedComponents/TabSwitcher/trackingTab';
import { useIsDesktop } from '@utils/helpers';

import messages from '../../../messages';
import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';
import { TabIds } from '../../model';
import { useProductDetailContext } from '../../ProductDetailContext';
import { Pictograms } from '../Pictograms';
import { EPictograms } from '../Pictograms/model';
import { useTabsWithExpandableText } from '../ProductTabContentProvider';
import { TRACKING_ATTRIBUTES } from '../tracking';

import { Characteristic } from './Characteristic';
import { Instructions } from './Instructions';
import { VideoInstructions } from './Instructions/VideoInstructions';
import LivestreamBannersCarousel from './LivestreamBannersCarousel';
import {
  Container,
  MarginWrapper,
  Description,
  CharTitle,
  CharactericticsWrapper,
  CharacteristicsTable,
  SectionTitle,
  HairSensitivityTestLink,
  CharacteristicsTableWrapper,
  LivestreamsContainer,
} from './styled';

export interface IAboutProductProps {
  isForSensitiveHair?: boolean;
  description: string;
  pictograms?: EPictograms[];
  characteristics: ICharacteristic[];
  composition: ICharacteristic[];
  isPerfume?: boolean;
  documentation?: Omit<
    GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number]['documentation'],
    '__typename'
  >;
}

export const AboutProduct: React.FC<IAboutProductProps> = React.memo(
  ({
    description,
    characteristics,
    composition,
    isPerfume,
    documentation,
    pictograms,
    isForSensitiveHair,
  }) => {
    const { formatMessage } = useIntl();
    const { getTimeFromInit } = useTrackingContext();

    const { currentVariant } = useProductDetailContext();
    const { livestreamOnPdEnabled } = useFeatureFlags();
    const [livestreamsRef, livestreamsInView] = useInView({
      triggerOnce: true,
      rootMargin: '100px',
    });

    const newPdDesignEnabled = useNewPdDesignEnabled();

    const isDesktop = useIsDesktop({ isNewPdDesign: newPdDesignEnabled });

    const { data, error } = useLivestreams({
      enabled: livestreamOnPdEnabled && isDesktop && livestreamsInView,
      productCode: currentVariant.productCode,
    });

    const shouldShowLivestreams = Boolean(
      livestreamOnPdEnabled &&
        isDesktop &&
        !error &&
        data?.getLivestreams?.shows &&
        data?.getLivestreams?.shows.length !== 0
    );

    const { tabsWithExpandableTextState, toggleFullText } =
      useTabsWithExpandableText();

    const descriptionWithExpandableText =
      tabsWithExpandableTextState?.[TabIds.Description];

    const hasPictograms = Boolean(pictograms?.length > 0);

    const shouldShowFullText =
      !hasPictograms ||
      descriptionWithExpandableText?.shouldShowFullText ||
      !descriptionWithExpandableText?.isTabOpenForTheFirstTime;

    const productCharacteristics = isPerfume
      ? composition.concat(characteristics)
      : characteristics;

    const hasDescription = !isEmpty(description);
    const hasCharacteristics = productCharacteristics.length > 0;

    const hasDetailedInstructions =
      documentation && (documentation.instructions || documentation.document);

    const hasDescriptionContent = Boolean(
      hasDescription ||
        hasDetailedInstructions ||
        documentation?.preparationUsage
    );

    if (!hasCharacteristics && !hasDescriptionContent) {
      return null;
    }

    const onToggleFullText = () => {
      toggleFullText(TabIds.Description);
      trackTab({
        mode: 'open partial',
        timing: getTimeFromInit(),
        trackingLabel: TRACKING_ATTRIBUTES.description,
      });
    };

    const pdCharacteristics = hasCharacteristics && (
      <CharactericticsWrapper
        fullWidth={!hasDescription}
        newPdDesignEnabled={newPdDesignEnabled}
        id="pdCharacteristics"
      >
        {hasDescription && (
          <CharTitle newPdDesignEnabled={newPdDesignEnabled}>
            <FormattedMessage
              {...messages[`composition${isPerfume ? 'Perfume' : 'Cosmetics'}`]}
            />
          </CharTitle>
        )}

        <CharacteristicsTable>
          <tbody>
            {productCharacteristics.map((item) => (
              <Characteristic
                key={item.name}
                item={item}
                fullWidth={!hasDescription}
              />
            ))}
          </tbody>
        </CharacteristicsTable>
      </CharactericticsWrapper>
    );

    return (
      <>
        <Container id="pdDescription" newPdDesignEnabled={newPdDesignEnabled}>
          <MarginWrapper
            fullWidth={!hasCharacteristics}
            newPdDesignEnabled={newPdDesignEnabled}
            hidden={!hasDescriptionContent}
            id="pd-description-wrapper"
          >
            {hasPictograms && <Pictograms pictograms={pictograms} />}

            {newPdDesignEnabled && hasCharacteristics && (
              <CharacteristicsTableWrapper>
                <CharacteristicsTable newPdDesignEnabled={newPdDesignEnabled}>
                  <tbody>
                    {productCharacteristics.map((item) => (
                      <Characteristic
                        key={item.name}
                        item={item}
                        fullWidth={true}
                      />
                    ))}
                  </tbody>
                </CharacteristicsTable>
              </CharacteristicsTableWrapper>
            )}

            <ExpandableText
              onClick={onToggleFullText}
              shouldShowFullText={shouldShowFullText}
              newPdDesignEnabled={newPdDesignEnabled}
              buttonText={<FormattedMessage {...messages.showMoreText} />}
            >
              {hasDescription && (
                <Description
                  newDesign={newPdDesignEnabled}
                  id="pd-description-text"
                  data-testid="pd-description-text"
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                />
              )}

              {hasDetailedInstructions && (
                <>
                  <SectionTitle newPdDesignEnabled={newPdDesignEnabled}>
                    <FormattedMessage {...messages.instructions} />
                  </SectionTitle>
                  <Instructions
                    instructionDocument={documentation.document}
                    instructions={documentation.instructions}
                  />
                </>
              )}

              {documentation?.video && (
                <>
                  <SectionTitle newPdDesignEnabled={newPdDesignEnabled}>
                    <FormattedMessage {...messages.videoInstructions} />
                  </SectionTitle>
                  <VideoInstructions video={documentation.video} />
                </>
              )}

              {documentation?.preparationUsage && (
                <>
                  <SectionTitle newPdDesignEnabled={newPdDesignEnabled}>
                    <FormattedMessage {...messages.preparationUsage} />
                  </SectionTitle>
                  <Description
                    newDesign={newPdDesignEnabled}
                    dangerouslySetInnerHTML={{
                      __html: documentation.preparationUsage,
                    }}
                  />
                </>
              )}

              {isForSensitiveHair && (
                <HairSensitivityTestLink
                  href={formatMessage(messages.sensitiveHairWarningUrl)}
                  rel="nofollow"
                  target="_blank"
                >
                  <FormattedMessage {...messages.sensitiveHairWarningText} />
                </HairSensitivityTestLink>
              )}

              {!isDesktop && !newPdDesignEnabled && pdCharacteristics}
            </ExpandableText>
          </MarginWrapper>

          {isDesktop && !newPdDesignEnabled && pdCharacteristics}
        </Container>

        <LivestreamsContainer
          ref={livestreamsRef}
          newPdDesignEnabled={newPdDesignEnabled}
        >
          {shouldShowLivestreams && (
            <>
              <SectionTitle newPdDesignEnabled={newPdDesignEnabled}>
                <FormattedMessage {...messages.fixedHeaderLivestreams} />
              </SectionTitle>
              <LivestreamBannersCarousel
                livestreams={data?.getLivestreams?.shows}
              />
            </>
          )}
        </LivestreamsContainer>
      </>
    );
  }
);
